import React, { useState, useEffect, useRef } from 'react'
import { useDebouncedEffect } from '@/utils/useDebouncedEffect'
import Pagination from '@/shared/Pagination'
import Loading from '@/shared/Loading'
import { useGlobalState } from '@/state'
import { EyeIcon, SortAscendingIcon, TagIcon } from '@heroicons/react/solid'
import useQuery from '@/hooks/useQuery'
import useDropdown from '@/hooks/useDropdown'
import useModal from '@/hooks/useModalV2'
import Modal from '@/shared/Modal'
import DefaultButton from '@/shared/Buttons/Default'
import Card from '@/shared/Card'
import SubmissionRow from '@/pages/ManageProjects/SubmissionRow'
import BucketsManager from '@/pages/ManageProjects/BucketsManager'
import BucketsIntroModal from '@/pages/ManageProjects/BucketsIntroModal'
import Tabs from '@/shared/Tabs'

const BUCKETS_INTRO_STORAGE_KEY = 'cccBucketsIntroSeen'

const NoResults = () => {
  return (
    <span>
      <div className="relative block w-full border-2 border-gray-300 dark:border-gray-600 border-dashed rounded-lg p-12 text-center hover:border-gray-400 dark:hover:border-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cccblue" >
        <EyeIcon className="mx-auto h-12 w-12 text-gray-400" />
        <span className="mt-2 block text-sm font-medium text-gray-900 dark:text-gray-300"> This person hasn't listed any submissions as visible on their profile.</span>
      </div>
    </span>
  )
}

const SubmissionsIndex = () => {
  const [manageProjectContainer] = useGlobalState('manageProjectContainer')
  const { project } = manageProjectContainer
  const [currentUser] = useGlobalState('currentUser')
  const user = currentUser
  const [meta, setMeta] = useState({})
  const [submissions, setSubmissions] = useState([])
  const { getRequest } = useQuery()
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [contains, setContains] = useState(null)
  const [reviewStatuses, setReviewStatuses] = useState([])
  const [order, setOrder] = useState('updated_at')
  const [role, setRole] = useState('all')
  const [roles, setRoles] = useState([])
  const [reviewStatus, setReviewStatus] = useState('unsorted')
  const [searchOpen, setSearchOpen] = useState(false)
  const [buckets, setBuckets] = useState([])
  const [bucketFilter, setBucketFilter] = useState('all')
  const scrollRef = useRef(null)
  const dropdownRef = useRef(false)
  const { dropdownOpen, setDropdownOpen, openDropdown } = useDropdown(dropdownRef)
  const bucketsModal = useModal()
  const bucketsIntroModal = useModal()
  const [introSeen, setIntroSeen] = useState(typeof window !== 'undefined' && window.localStorage?.getItem(BUCKETS_INTRO_STORAGE_KEY) === '1')
  const pendingActionRef = useRef(null)

  useDebouncedEffect(() => {
    setDropdownOpen(false)
    fetchData(true)
  }, 200, [contains, order, reviewStatus, role, bucketFilter])

  useEffect(() => {
    getRequest(`/api/v3/manage/projects/${project.id}/roles`, {}, (err, jsonData) => {
      setLoading(false)
      if (err) { /* hooks */ return }

      setRoles(jsonData.roles)
    })
    getRequest(`/api/v3/manage/projects/${project.id}/buckets`, {}, (err, jsonData) => {
      if (err) { return }
      setBuckets(jsonData.buckets)
    })
  }, [])

  const updateSubmissionBucket = (submissionId, bucketId) => {
    setSubmissions(submissions.map((s) => s.id === submissionId ? { ...s, bucketId } : s))
  }

  const showIntroOrRun = (action) => {
    if (introSeen) { action(); return }
    pendingActionRef.current = action
    bucketsIntroModal.openModal()
  }

  const dismissIntro = () => {
    if (typeof window !== 'undefined') { window.localStorage?.setItem(BUCKETS_INTRO_STORAGE_KEY, '1') }
    setIntroSeen(true)
    bucketsIntroModal.closeModal()
    const next = pendingActionRef.current
    pendingActionRef.current = null
    if (next) { next() }
  }

  useEffect(() => { fetchData() }, [page])

  const fetchData = (newPage) => {
    setLoading(true)
    let data = {
      contains: contains,
      order_by: order,
      review_status: reviewStatus,
      page: newPage ? 1 : page
    }
    if (role !== 'all') { data['role_id'] = role }
    if (bucketFilter !== 'all') { data['bucket_id'] = bucketFilter }
    getRequest(`/api/v3/manage/projects/${project.id}/submissions`, data, (err, jsonData) => {
      setLoading(false)
      if (err) { /* hooks */ return }

      setReviewStatuses(jsonData.reviewStatuses)
      setSubmissions(jsonData.submissions)
      setMeta(jsonData.meta)
    })
  }
  const handleChange = (e) => {
    if (e.target.name === 'order') {
      setOrder(e.target.value)
    }
    if (e.target.name === 'contains') {
      setContains(e.target.value)
    }
  }

  const changeTab = (tab) => { setReviewStatus(tab) }

  const tablist = [
    { name: 'Unsorted', count: reviewStatuses?.unsorted, changeValue: 'unsorted' },
    { name: 'Assigned', count: reviewStatuses?.assigned, changeValue: 'assigned' },
    { name: 'Shortlisted', count: reviewStatuses?.shortlisted, changeValue: 'shortlisted' },
    { name: 'Dismissed', count: reviewStatuses?.dismissed, changeValue: 'dismissed' }
  ]

  const orderMap = {
    updated_at: 'Latest Activity',
    created_at: 'Created At',
    played_count: 'Listened To Count',
    cached_votes_up: 'Most Upvoted',
    comments_count: 'Most Commented'
  }

  return <>
    <div className='mt-1' />
    <Card title='Project Submissions'>
      <>
        <div ref={scrollRef}>
          <div className='flex flex-col gap-2 lg:flex-row lg:justify-between lg:items-center'>
            <Tabs statuses={reviewStatuses} tablist={tablist} currentTab={reviewStatus} changeTab={changeTab} />
            <div className='flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:items-center'>
              <div className='relative flex items-center'>
                <button onClick={openDropdown} className='py-1 px-2 text-xs sm:text-sm text-gray-900 bg-white rounded-lg border border-gray-200 hover:bg-gray-100 hover:text-cccblue focus:ring-2 focus:ring-blue-700 focus:text-cccblue dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:hover:text-white dark:hover:bg-gray-600 dark:focus:ring-blue-500 dark:focus:text-white flex space-x-1 items-center w-full sm:w-48'>
                  <span className="sr-only" hidden>Open options</span>
                  <SortAscendingIcon className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden="true" />
                  <span className='truncate'>{orderMap[order]}</span>
                </button>
                { dropdownOpen && <div ref={dropdownRef} tabIndex="-1" className={`absolute right-0 z-30 ${dropdownOpen ? '' : 'hidden opacity-0 scale-0'} py-2 overflow-y-auto transition origin-top-right bg-white border border-gray-900 dark:border-gray-100 rounded-md shadow-md dark:bg-gray-800 top-6 max-h-80`}>
                  <div onClick={() => setOrder('updated_at')} className='text-gray-700 dark:text-gray-100 flex px-4 py-2 text-sm cursor-pointer hover:text-cccblue-alt dark:hover:text-cccblue-alt' >{orderMap.updated_at}</div>
                  <div onClick={() => setOrder('created_at')} className='text-gray-700 dark:text-gray-100 flex px-4 py-2 text-sm cursor-pointer hover:text-cccblue-alt dark:hover:text-cccblue-alt' >{orderMap.created_at}</div>
                  <div onClick={() => setOrder('played_count')} className='text-gray-700 dark:text-gray-100 flex px-4 py-2 text-sm cursor-pointer hover:text-cccblue-alt dark:hover:text-cccblue-alt' >{orderMap.played_count}</div>
                  <div onClick={() => setOrder('cached_votes_up')} className='text-gray-700 dark:text-gray-100 flex px-4 py-2 text-sm cursor-pointer hover:text-cccblue-alt dark:hover:text-cccblue-alt' >{orderMap.cached_votes_up}</div>
                  <div onClick={() => setOrder('comments_count')} className='text-gray-700 dark:text-gray-100 flex px-4 py-2 text-sm cursor-pointer hover:text-cccblue-alt dark:hover:text-cccblue-alt' >{orderMap.comments_count}</div>
                </div> }
              </div>
              <select id="tabs" name="tabs" className="py-1 px-2 text-xs sm:text-sm text-gray-900 bg-white rounded-lg border border-gray-200 hover:bg-gray-100 hover:text-cccblue focus:ring-2 focus:ring-blue-700 focus:text-cccblue dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:hover:text-white dark:hover:bg-gray-600 dark:focus:ring-blue-500 dark:focus:text-white flex space-x-1 items-center w-full sm:w-48" defaultValue={role} onChange={(e) => setRole(e.target.value)}>
                <option value={'all'}>--- Filter Role ---</option>
                {roles.map((pr) => (
                  <option key={`prfilter${pr.name}`} value={pr.id}>{pr.name}</option>
                ))}
              </select>
              <select aria-label='Filter by bucket' value={bucketFilter} onChange={(e) => setBucketFilter(e.target.value)} className="py-1 px-2 text-xs sm:text-sm text-gray-900 bg-white rounded-lg border border-gray-200 hover:bg-gray-100 hover:text-cccblue focus:ring-2 focus:ring-blue-700 focus:text-cccblue dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:hover:text-white dark:hover:bg-gray-600 dark:focus:ring-blue-500 dark:focus:text-white flex space-x-1 items-center w-full sm:w-48">
                <option value={'all'}>--- Filter Bucket ---</option>
                <option value={'none'}>(no bucket)</option>
                {buckets.map((b) => (
                  <option key={`bktfilter${b.id}`} value={b.id}>{b.name}</option>
                ))}
              </select>
              <DefaultButton
                onClick={() => showIntroOrRun(bucketsModal.openModal)}
                text={
                  <div className='flex items-center space-x-1'>
                    <TagIcon className='h-4 w-4' />
                    <span>Manage Buckets</span>
                  </div>
                }
              />
            </div>
          </div>
          <Modal isOpen={bucketsModal.isOpen} closeModal={bucketsModal.closeModal} title='Manage Buckets'>
            <div className='text-gray-900 dark:text-gray-100 mt-4'>
              <BucketsManager
                projectId={project.id}
                buckets={buckets}
                setBuckets={setBuckets}
                onShowIntro={bucketsIntroModal.openModal}
              />
              <div className='flex justify-end mt-4'>
                <DefaultButton onClick={bucketsModal.closeModal} text='Done' />
              </div>
            </div>
          </Modal>
          <BucketsIntroModal isOpen={bucketsIntroModal.isOpen} onDismiss={dismissIntro} />
          { searchOpen && <div className='flex flex-col sm:flex-row mb-2'>
            <input name='contains' type="text" onChange={handleChange}
              className="shadow-sm focus:ring-cccpurple dark:bg-gray-900 dark:text-white focus:border-cccpurple block w-full sm:text-sm border-gray-300 dark:border-gray-600 rounded-md"
              placeholder="Search by project name or role name. ex: Goku"
            />
          </div> }
          { submissions.length >= 0 && <div className='mt-2'><Pagination meta={meta} callback={setPage} scrollRef={scrollRef} /></div> }
        </div>
        { user.auditionsCount === 0 && <NoResults /> }
        { loading && <Loading /> }
        <ul className="space-y-3">
          {submissions.map((sub) => (
            <SubmissionRow
              key={`pubsub${sub.id}${sub.reviewStatus}`}
              submission={sub}
              voteIds={[]}
              buckets={buckets}
              onBucketChange={(bucketId) => updateSubmissionBucket(sub.id, bucketId)}
              showIntroOrRun={showIntroOrRun}
            />
          ))}
        </ul>
        { submissions.length >= 0 && <div className='mt-2'><Pagination meta={meta} callback={setPage} scrollRef={scrollRef} /></div> }
      </>
    </Card>
  </>
}

export default SubmissionsIndex
